import { RowCrudStatus } from "../../common/types";
import {
  MusicalWork,
  MusicalWorkCreateMutationVariables,
  MusicalWorkUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { musicalGenreDisplayId } from "../../musicalGenre/store/MusicalGenreCommonStore";
import {
  WorkAuthorValuesType,
  getWorkAuthorValues,
  getWorkAuthorCreateInput,
  getWorkAuthorUpdateInput,
} from "../../workAuthor/store/WorkAuthorCommonStore";
import {
  addedStatusList,
  originalDeletedStatusList,
  originalUpdatedStatusList,
} from "../../common/types";
import { blankToNull } from "../../../common/blankToNull";
import { interpreterDisplayId } from "../../interpreter/store/InterpreterCommonStore";
import { territoryDisplayId } from "../../territory/store/TerritoryCommonStore";
import { FileCRUDType, fileCRUDInit } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { getFileVariableValue } from "../../common/utils";
import {
  PhonogramValuesType,
  getPhonogramValues,
  getPhonogramCreateInput,
  getPhonogramUpdateInput,
} from "../../phonogram/store/PhonogramCommonStore";

let uniqueCounter = 1;
export type MusicalWorkValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  title: string;
  __musicalGenre: string;
  showMusicalGenre: { displayId: string; id: string } | null;
  alternativeTitles: string[];
  showAuthors: WorkAuthorValuesType[];
  notes: string;
  __performers: string[];
  showPerformers: { displayId: string; id: string }[];
  cmsId: string;
  __territories: string[];
  showTerritories: { displayId: string; id: string }[];
  iswc: string;
  workNumber: number;
  score: string;
  score_data: FileCRUDType;
  __score_files: FileCRUDType[];
  showPhonograms: PhonogramValuesType[];
  originalLyrics: string;
  contractObservation: string;
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
};

export type MusicalWorkErrorsType = {
  title?: { message: string; fieldLabel: string };
  musicalGenre?: { message: string; fieldLabel: string };
  alternativeTitles?: { message: string; fieldLabel: string };
  authors?: { message: string; fieldLabel: string };
  notes?: { message: string; fieldLabel: string };
  performers?: { message: string; fieldLabel: string };
  cmsId?: { message: string; fieldLabel: string };
  territories?: { message: string; fieldLabel: string };
  iswc?: { message: string; fieldLabel: string };
  workNumber?: { message: string; fieldLabel: string };
  score?: { message: string; fieldLabel: string };
  phonograms?: { message: string; fieldLabel: string };
  originalLyrics?: { message: string; fieldLabel: string };
  contractObservation?: { message: string; fieldLabel: string };
};

export function MusicalWorkValuesInit(): MusicalWorkValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    title: "",
    __musicalGenre: "",
    showMusicalGenre: null,
    alternativeTitles: [],
    showAuthors: [],
    notes: "",
    __performers: [],
    showPerformers: [],
    cmsId: "",
    __territories: [],
    showTerritories: [],
    iswc: "",
    workNumber: 0,
    score: "",
    score_data: fileCRUDInit(),
    __score_files: [],
    showPhonograms: [],
    originalLyrics: "",
    contractObservation: "",
    createdAt_: null,
    lastUpdatedAt_: null,
  };
}

export type MusicalWorkType = MusicalWork | MusicalWorkValuesType;

export function musicalWorkDisplayId(
  source: Partial<MusicalWork>,
  _langStore: LangStore
) {
  return source.title ?? _langStore.emptyStringSymbol;
}

export function getMusicalWorkValues(
  source: MusicalWorkType,
  _langStore: LangStore,
  _duplicated: boolean = false
): MusicalWorkValuesType {
  const entityValues = MusicalWorkValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : musicalWorkDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.title = source.title;
  entityValues.showMusicalGenre =
    "musicalGenre" in source && source.musicalGenre
      ? {
          id: source.musicalGenre.id,
          displayId: musicalGenreDisplayId(source.musicalGenre, _langStore),
        }
      : "showMusicalGenre" in source && source.showMusicalGenre
      ? {
          id: source.showMusicalGenre.id,
          displayId: source.showMusicalGenre.displayId,
        }
      : null;
  entityValues.__musicalGenre = entityValues.showMusicalGenre?.displayId || "";
  entityValues.alternativeTitles = source.alternativeTitles || [];
  entityValues.showAuthors =
    "authors" in source && source.authors
      ? (source.authors || []).map((x) => {
          const row: WorkAuthorValuesType = getWorkAuthorValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showAuthors" in source && source.showAuthors
      ? (source.showAuthors || []).map((x) => {
          const row: WorkAuthorValuesType = getWorkAuthorValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.notes = source.notes || "";
  entityValues.showPerformers =
    "performers" in source && source.performers
      ? (source.performers || []).map((x) => {
          return { id: x.id, displayId: interpreterDisplayId(x, _langStore) };
        })
      : "showPerformers" in source && source.showPerformers
      ? (source.showPerformers || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__performers = entityValues.showPerformers.map(
    (x) => x.displayId
  );
  entityValues.cmsId = source.cmsId || "";
  entityValues.showTerritories =
    "territories" in source && source.territories
      ? (source.territories || []).map((x) => {
          return { id: x.id, displayId: territoryDisplayId(x, _langStore) };
        })
      : "showTerritories" in source && source.showTerritories
      ? (source.showTerritories || []).map((x) => {
          return { id: x.id, displayId: x.displayId };
        })
      : [];
  entityValues.__territories = entityValues.showTerritories.map(
    (x) => x.displayId
  );
  entityValues.iswc = source.iswc || "";
  entityValues.workNumber = source.workNumber || 0;
  entityValues.__score_files =
    source.score_data && !_duplicated
      ? [
          {
            id:
              "id" in source.score_data && source.score_data.id
                ? source.score_data.id
                : `generated_${uniqueCounter++}`,
            name: source.score_data.name,
            description: source.score_data.description || undefined,
            size: source.score_data.size || 0,
            formattedSize: source.score_data.size
              ? formatBytes(source.score_data.size)
              : "",
            mimetype: source.score_data.mimetype,
            url: source.score_data.url || undefined,
            __status__: "original",
          },
        ]
      : [];
  entityValues.score = source.score && !_duplicated ? source.score : "";
  entityValues.score_data =
    source.score_data && "__status__" in source.score_data
      ? source.score_data
      : source.score_data
      ? {
          id: ("id" in source.score_data && source.score_data.id
            ? source.score_data.id
            : `generated_${uniqueCounter++}`) as string,
          name: source.score_data.name,
          description: source.score_data.description || undefined,
          size: source.score_data.size || 0,
          formattedSize: source.score_data.size
            ? formatBytes(source.score_data.size)
            : "",
          mimetype: source.score_data.mimetype,
          url: source.score_data.url || undefined,
          __status__: "original" as RowCrudStatus,
        }
      : fileCRUDInit();
  entityValues.showPhonograms =
    "phonograms" in source && source.phonograms
      ? (source.phonograms || []).map((x) => {
          const row: PhonogramValuesType = getPhonogramValues(
            x,
            _langStore,
            _duplicated
          );
          return { ...row, __status__: _duplicated ? "added" : "original" };
        })
      : "showPhonograms" in source && source.showPhonograms
      ? (source.showPhonograms || []).map((x) => {
          const row: PhonogramValuesType = getPhonogramValues(
            x,
            _langStore,
            _duplicated
          );
          // Fila de grilla ya existente: preservar su __status__ (una fila "added"
          // reseteada a "original" se clasifica en update[] con id temporal y sin
          // version_ — el server la rechaza). "original" solo como fallback para
          // filas sin status real (init "empty").
          return {
            ...row,
            __status__: _duplicated
              ? "added"
              : row.__status__ && row.__status__ !== "empty"
              ? row.__status__
              : "original",
          };
        })
      : [];
  entityValues.originalLyrics = source.originalLyrics || "";
  entityValues.contractObservation = source.contractObservation || "";
  entityValues.createdAt_ = source.createdAt_
    ? new Date(source.createdAt_)
    : null;
  entityValues.lastUpdatedAt_ = source.lastUpdatedAt_
    ? new Date(source.lastUpdatedAt_)
    : null;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getMusicalWorkUpdateVariables(
  values: MusicalWorkValuesType
): MusicalWorkUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    title: values.title,
    musicalGenreId: values.showMusicalGenre?.id || "",
    alternativeTitles: values.alternativeTitles || null,
    authors: {
      create: values.showAuthors
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getWorkAuthorCreateInput(x)),
      update: values.showAuthors
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getWorkAuthorUpdateInput(x)),
      remove: values.showAuthors
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
    notes: blankToNull(values.notes),
    performersIds: values.showPerformers
      ? { set: values.showPerformers.map((x) => x.id) }
      : undefined,
    cmsId: blankToNull(values.cmsId),
    territoriesIds: values.showTerritories
      ? { set: values.showTerritories.map((x) => x.id) }
      : undefined,
    iswc: blankToNull(values.iswc),
    score: getFileVariableValue(values.__score_files),
    phonograms: {
      create: values.showPhonograms
        .filter((x) => addedStatusList.includes(x.__status__))
        .map((x) => getPhonogramCreateInput(x)),
      update: values.showPhonograms
        .filter((x) => originalUpdatedStatusList.includes(x.__status__))
        .map((x) => getPhonogramUpdateInput(x)),
      remove: values.showPhonograms
        .filter((x) => originalDeletedStatusList.includes(x.__status__))
        .map((x) => x.id),
    },
    originalLyrics: blankToNull(values.originalLyrics),
    contractObservation: blankToNull(values.contractObservation),
  };
}

export function getMusicalWorkCreateVariables(
  values: MusicalWorkValuesType
): MusicalWorkCreateMutationVariables {
  return {
    title: values.title,
    musicalGenreId: values.showMusicalGenre?.id || "",
    alternativeTitles: values.alternativeTitles || null,
    authors: values.showAuthors
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getWorkAuthorCreateInput(x)),
    notes: blankToNull(values.notes),
    performersIds: values.showPerformers
      ? values.showPerformers.map((x) => x.id)
      : undefined,
    cmsId: blankToNull(values.cmsId),
    territoriesIds: values.showTerritories.map((x) => x.id),
    iswc: blankToNull(values.iswc),
    score: getFileVariableValue(values.__score_files),
    phonograms: values.showPhonograms
      .filter((x) => addedStatusList.includes(x.__status__))
      .map((x) => getPhonogramCreateInput(x)),
    originalLyrics: blankToNull(values.originalLyrics),
    contractObservation: blankToNull(values.contractObservation),
  };
}
